import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ESpinner from 'components/others/Spinner';
import { createPdfBlob } from 'utils/helpers/pdf/pdfMake';
import type { ProposalEditFormValues } from '../Proposal/types';
import { buildProposalPdfDefinition } from './model/helper/proposalPdfDefinition';
import { fileToPdfCompatibleDataUrl } from './model/helper/fileToPdfCompatibleDataUrl';
import useTemplateValueKeys from './model/useTemplateValueKeys';
import PdfDocumentView from './PdfDocumentView';

interface ProposalPreviewTabProps {
    formData: ProposalEditFormValues;
    rate: number;
    translationNs?: string;
    showTitlePage?: boolean;
    /** Documents insert the items table only when the template has a marker key. */
    appendSpecificationWithoutMarker?: boolean;
    objectData?: unknown;
    /** True only while the preview tab is the selected editor tab. */
    active?: boolean;
}

const ProposalPreviewTab = ({
    formData,
    rate,
    translationNs = 'Proposal',
    showTitlePage = false,
    appendSpecificationWithoutMarker = true,
    objectData,
    active = false,
}: ProposalPreviewTabProps) => {
    const { t } = useTranslation([translationNs, 'common', 'Products']);
    const templateValueKeys = useTemplateValueKeys();
    const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
    const [phase, setPhase] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
    const [rendered, setRendered] = useState(false);
    const requestRef = useRef(0);

    const formDataRef = useRef(formData);
    const rateRef = useRef(rate);
    const objectDataRef = useRef(objectData);
    const showTitlePageRef = useRef(showTitlePage);
    const appendSpecificationWithoutMarkerRef = useRef(appendSpecificationWithoutMarker);
    const templateValueKeysRef = useRef(templateValueKeys);
    const tRef = useRef(t);
    formDataRef.current = formData;
    rateRef.current = rate;
    objectDataRef.current = objectData;
    showTitlePageRef.current = showTitlePage;
    appendSpecificationWithoutMarkerRef.current = appendSpecificationWithoutMarker;
    templateValueKeysRef.current = templateValueKeys;
    tRef.current = t;

    useEffect(() => {
        if (!active) {
            requestRef.current += 1;
            setPdfBytes(null);
            setRendered(false);
            setPhase('idle');
            return;
        }

        const requestId = requestRef.current + 1;
        requestRef.current = requestId;
        setPdfBytes(null);
        setRendered(false);
        setPhase('loading');

        const run = async () => {
            const currentForm = formDataRef.current;
            const hasLogo = Boolean(String(currentForm.proposalLogo || '').trim());
            const hasBackground = Boolean(String(currentForm.proposalBackground || '').trim());
            const translate = tRef.current;
            const docDefinition = await buildProposalPdfDefinition({
                t: (key: string, options?: Record<string, unknown>) => translate(key, options || {}),
                templateValueKeys: templateValueKeysRef.current,
                formData: currentForm,
                rate: rateRef.current,
                generatedAt: Date.now(),
                logoDataUrl: hasLogo
                    ? await fileToPdfCompatibleDataUrl(String(currentForm.proposalLogo || ''))
                    : '',
                backgroundDataUrl: hasBackground
                    ? await fileToPdfCompatibleDataUrl(String(currentForm.proposalBackground || ''))
                    : '',
                resolveImageDataUrl: fileToPdfCompatibleDataUrl,
                objectData: objectDataRef.current,
                includeCoverPage: showTitlePageRef.current,
                appendSpecificationWithoutMarker: appendSpecificationWithoutMarkerRef.current,
            });
            const blob = await createPdfBlob(docDefinition);
            return new Uint8Array(await blob.arrayBuffer());
        };

        run()
            .then((bytes) => {
                if (requestRef.current !== requestId) return;
                setPdfBytes(bytes);
                setPhase('ready');
            })
            .catch((error) => {
                console.error(error);
                if (requestRef.current !== requestId) return;
                setPdfBytes(null);
                setPhase('error');
            });
    }, [active]);

    const busy = phase === 'loading' || (phase === 'ready' && !rendered);

    return (
        <div className="proposal-pdf-preview-wrap">
            {busy ? (
                <div className="py-5">
                    <ESpinner />
                </div>
            ) : null}
            {phase === 'error' ? (
                <p className="text-danger mb-0">{t('common:messError')}</p>
            ) : null}
            {phase === 'ready' && pdfBytes ? (
                <PdfDocumentView
                    data={pdfBytes}
                    onError={() => setPhase('error')}
                    onReady={() => setRendered(true)}
                />
            ) : null}
        </div>
    );
};

export default ProposalPreviewTab;
